import React from 'react';
import { useTranslation } from 'react-i18next';

const acts = [
  {
    category: 'Acts',
    items: [
      {
        title: 'A.P. Town Planning Act, 1920',
        description: 'The primary legislation governing town planning in Andhra Pradesh. Provides for the preparation of town planning schemes and regulation of building activities.',
        icon: 'ri-scales-3-line',
      },
      {
        title: 'A.P. Urban Areas (Development) Act, 1975',
        description: 'Establishes Urban Development Authorities (UDAs) for planned development of urban areas, including preparation of master plans and zonal development plans.',
        icon: 'ri-scales-3-line',
      },
      {
        title: 'A.P. Municipalities Act, 1965',
        description: 'Provides for the constitution and administration of municipalities, including building regulations and land use controls within municipal limits.',
        icon: 'ri-scales-3-line',
      },
      {
        title: 'A.P. Municipal Corporations Act, 1955',
        description: 'Governs the administration of municipal corporations including town planning functions, building permissions, and development control.',
        icon: 'ri-scales-3-line',
      },
      {
        title: 'A.P. Panchayat Raj Act, 1994',
        description: 'Provides for building permission regulations in panchayat areas and rural planning provisions.',
        icon: 'ri-scales-3-line',
      },
      {
        title: 'A.P. Land Grabbing (Prohibition) Act, 1982',
        description: 'Prohibits land grabbing activities and provides for special courts to deal with land disputes in urban and rural areas.',
        icon: 'ri-scales-3-line',
      },
    ],
  },
  {
    category: 'Rules & Regulations',
    items: [
      {
        title: 'A.P. Building Rules, 2017',
        description: 'Comprehensive rules governing building construction including setbacks, FAR, height restrictions, parking requirements, and structural safety norms.',
        icon: 'ri-file-list-3-line',
      },
      {
        title: 'Development Control Rules & Regulations',
        description: 'Rules regulating development activities including land use classification, building heights, open spaces, and infrastructure requirements.',
        icon: 'ri-file-list-3-line',
      },
      {
        title: 'Master Plan Zoning Regulations',
        description: 'Zoning regulations for master plan areas defining permissible land uses, density norms, and development standards for different zones.',
        icon: 'ri-file-list-3-line',
      },
      {
        title: 'Layout Rules & Regulations',
        description: 'Rules governing the approval of residential, commercial, and industrial layouts including road widths, open space requirements, and amenity provisions.',
        icon: 'ri-file-list-3-line',
      },
      {
        title: 'Building Rules under Municipalities Act',
        description: 'Building permission rules applicable within municipal areas governing construction of new buildings, additions, and alterations.',
        icon: 'ri-file-list-3-line',
      },
    ],
  },
  {
    category: 'Guidelines',
    items: [
      {
        title: 'Guidelines for Layout Regularisation Scheme (LRS)',
        description: 'Guidelines for regularisation of unauthorized layouts including eligibility criteria, application process, fee structure, and conditions.',
        icon: 'ri-draft-line',
      },
      {
        title: 'Guidelines for Building Penalisation Scheme (BPS)',
        description: 'Guidelines for penalisation of unauthorized buildings including assessment of violations, penalty calculations, and regularisation process.',
        icon: 'ri-draft-line',
      },
      {
        title: 'Guidelines for Change of Land Use (CLU)',
        description: 'Procedures and conditions for conversion of land from one use to another, including agricultural to non-agricultural (NALA) conversions.',
        icon: 'ri-draft-line',
      },
      {
        title: 'UDA Formation & Functioning Guidelines',
        description: 'Guidelines for the constitution, functioning, and jurisdiction of Urban Development Authorities across Andhra Pradesh.',
        icon: 'ri-draft-line',
      },
      {
        title: 'Green Building & Sustainability Guidelines',
        description: 'Guidelines for incorporating sustainable design, energy efficiency, rainwater harvesting, and waste management in building construction.',
        icon: 'ri-draft-line',
      },
    ],
  },
];

export const ActsBylawsPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <a href="/resources" className="hover:text-amber-700 transition-colors">{t('header.resources')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.resourcesActsByLaws')}</span>
      </nav>

      {/* Page Header */}
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('header.resourcesActsByLaws')}</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          Key legislation, rules, regulations, and guidelines governing town planning and urban development in Andhra Pradesh.
        </p>
      </div>

      {/* Content */}
      {acts.map((section) => (
        <div key={section.category} className="mb-12">
          <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
            <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
            {section.category}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {section.items.map((item, index) => (
              <div
                key={index}
                className="group bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all duration-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
                    <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-amber-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-stone-800 font-semibold group-hover:text-amber-700 transition-colors mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-stone-500 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
